import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import {
  ScreenDesignTranslationService,
  TranslationFilterType
} from "../../services/screen-design-translation-service";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

const FILTER_MAP: Record<string, TranslationFilterType> = {
  all: TranslationFilterType.All,
  "exclude-not-translated": TranslationFilterType.ExcludeNotTranslated,
  "exclude-translated": TranslationFilterType.ExcludeTranslated
};

interface TranslationExportResult {
  success: true;
  downloadUrl: string;
  fileName: string;
  message: string;
}

interface TranslationErrorResult {
  error: string;
}

function buildProjectFileName(sdInstance: ScreenDesignInstance, language: string, suffix: string): string {
  const sd: IScreenDesignContextState = getValues(sdInstance);
  const projectName = sd?.project?.name?.toLowerCase().replace(/\s+/g, "_") || "project";
  return `${projectName}_${language.toLowerCase()}_${suffix}.xlsx`;
}

// A download link handed to the chat must stay valid long enough for the user to
// click it, then its underlying xlsx Blob must be released rather than pinned for
// the tab's lifetime. Revoke after a generous window.
const EXPORT_OBJECT_URL_TTL_MS = 5 * 60 * 1000;

export async function exportDesignerTranslations(
  sdInstance: ScreenDesignInstance,
  input: { language: string; filter?: string }
): Promise<TranslationExportResult | TranslationErrorResult> {
  const { language, filter } = input;
  if (!language) return { error: "language is required (e.g. 'tr_TR')" };

  const blob = await ScreenDesignTranslationService.exportTranslations(
    language,
    FILTER_MAP[filter || "all"] ?? TranslationFilterType.All
  );
  const downloadUrl = URL.createObjectURL(blob);
  setTimeout(() => URL.revokeObjectURL(downloadUrl), EXPORT_OBJECT_URL_TTL_MS);
  const fileName = buildProjectFileName(sdInstance, language, "translations");

  return {
    success: true,
    downloadUrl,
    fileName,
    message: `Translations exported. Show the user a markdown link: [${fileName}](${downloadUrl})`
  };
}

export async function exportDesignerTranslationTemplate(
  sdInstance: ScreenDesignInstance,
  input: { language: string }
): Promise<TranslationExportResult | TranslationErrorResult> {
  const { language } = input;
  if (!language) return { error: "language is required (e.g. 'tr_TR')" };

  const blob = await ScreenDesignTranslationService.exportExcelTemplate(language);
  const downloadUrl = URL.createObjectURL(blob);
  setTimeout(() => URL.revokeObjectURL(downloadUrl), EXPORT_OBJECT_URL_TTL_MS);
  const fileName = buildProjectFileName(sdInstance, language, "template");

  return {
    success: true,
    downloadUrl,
    fileName,
    message: `Template exported. Show the user a markdown link: [${fileName}](${downloadUrl})`
  };
}
